import { HttpErrorResponse } from '@angular/common/http';

export class ApiErrorResponse extends HttpErrorResponse
{
  private static readonly defaultMessages: Map<number, string> = new Map<number, string>([
    [0, 'No se pudo conectar con el servidor. Verifica tu conexión a internet.'],
    [400, 'La solicitud contiene datos inválidos.'],
    [401, 'Tu sesión ha expirado, inicia sesión nuevamente.'],
    [403, 'No tienes permisos para realizar esta acción.'],
    [404, 'No se encontró el recurso solicitado.'],
    [408, 'La solicitud tardó demasiado, intenta nuevamente.'],
    [500, 'Ocurrió un error en el servidor, intenta nuevamente.'],
    [503, 'El servicio no está disponible en este momento, intenta más tarde.'],
    [504, 'La solicitud tardó demasiado, intenta nuevamente.']
  ]);

  private static readonly genericMessage: string = 'Ocurrió un error inesperado, intenta nuevamente.';

  constructor(err: HttpErrorResponse)
  {
    super({
      error: err.error,
      headers: err.headers,
      status: err.status,
      statusText: err.statusText,
      url: err.url
    });
  }

  public static from(err: HttpErrorResponse): ApiErrorResponse
  {
    return err instanceof ApiErrorResponse ? err : new ApiErrorResponse(err);
  }

  public getErrorMessage(fallback: string = null): string
  {
    return this.readBody(this.error) || fallback || this.getDefaultMessage();
  }

  public getDefaultMessage(): string
  {
    return ApiErrorResponse.defaultMessages.get(this.status) || ApiErrorResponse.genericMessage;
  }

  private readBody(body: any): string
  {
    if (body == null) { return null; }

    if (typeof body === 'string') { return this.readTextBody(body); }

    if (typeof body === 'object') { return this.readObjectBody(body); }

    return null;
  }

  /**
  * The authentication interceptor forces responseType 'text' on urls containing '/update/',
  * so those bodies arrive as a string holding the serialized json.
  */
  private readTextBody(body: string): string
  {
    const text = body.trim();

    if (text === '') { return null; }

    try
    {
      const parsed = JSON.parse(text);

      return typeof parsed === 'string' ? this.cleanUp(parsed) : this.readObjectBody(parsed);
    }
    catch (e)
    {
      return text;
    }
  }

  private readObjectBody(body: any): string
  {
    if (body == null || typeof body !== 'object') { return null; }

    const validationMessage = this.readValidationErrors(body.errors);

    if (validationMessage) { return validationMessage; }

    return this.cleanUp(body.message)
      || this.cleanUp(body.Message)
      || this.cleanUp(body.detail)
      || this.cleanUp(body.title);
  }

  private readValidationErrors(errors: any): string
  {
    if (errors == null || typeof errors !== 'object') { return null; }

    const messages = [];

    Object.keys(errors).forEach(key =>
    {
      const value = errors[key];

      if (Array.isArray(value)) { messages.push(...value.filter(item => this.cleanUp(item))); }
      else if (this.cleanUp(value)) { messages.push(value); }
    });

    return messages.length > 0 ? messages.join(' ') : null;
  }

  private cleanUp(value: any): string
  {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
  }
}
